import { Button } from "./ui/button";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { SignUpDialog } from "./SignUpDialog";
import { LoginDialog } from "./LoginDialog";

// Define the allowed subscription tier types
type SubscriptionTier = "free" | "basic" | "growth" | "professional";

export const Pricing = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showSignUp, setShowSignUp] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const handleUpgrade = async (tier: SubscriptionTier) => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      setShowLogin(true);
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          subscription_tier: tier,
          subscription_updated_at: new Date().toISOString(),
          resources_used_this_month: 0 // Reset resource count when subscription changes
        })
        .eq('id', session.user.id);

      if (error) throw error;

      toast({
        title: "Subscription Updated!",
        description: `You have successfully upgraded to the ${tier} tier.`,
      });
    } catch (error) {
      toast({
        title: "Error updating subscription",
        description: "There was a problem updating your subscription. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="py-24 bg-gradient-to-b from-purple-50 to-white">
      <div className="container px-4 md:px-6">
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
            Choose Your <span className="hero-gradient">Perfect Plan</span>
          </h2>
          <p className="mx-auto max-w-[700px] text-gray-500 md:text-xl dark:text-gray-400">
            Select the plan that best fits your needs. Upgrade or downgrade at any time.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {/* Free Tier */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative p-6 space-y-6 bg-white rounded-lg border border-gray-200 hover:border-transparent transition-all duration-300">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">Free</h3>
                <p className="text-gray-500 text-sm">Perfect to get started</p>
                <div className="text-3xl font-bold">$0</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>5 resources per month</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>Basic analytics</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>Community support</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                onClick={() => setShowSignUp(true)}
              >
                Get Started
              </Button>
            </div>
          </div>

          {/* Basic Tier */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative p-6 space-y-6 bg-white rounded-lg border border-gray-200 hover:border-transparent transition-all duration-300">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">Basic</h3>
                <p className="text-gray-500 text-sm">For growing teams</p>
                <div className="text-3xl font-bold">$100<span className="text-lg font-normal">/mo</span></div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>10 resources per month</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>Advanced analytics</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>Priority email support</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                onClick={() => handleUpgrade('basic' as SubscriptionTier)}
              >
                Upgrade to Basic
              </Button>
            </div>
          </div>

          {/* Growth Tier */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative p-6 space-y-6 bg-white rounded-lg border border-gray-200 hover:border-transparent transition-all duration-300">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">Growth</h3>
                <p className="text-gray-500 text-sm">For scaling businesses</p>
                <div className="text-3xl font-bold">$250<span className="text-lg font-normal">/mo</span></div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>20 resources per month</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>Custom analytics</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>24/7 phone support</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                onClick={() => handleUpgrade('growth' as SubscriptionTier)}
              >
                Upgrade to Growth
              </Button>
            </div>
          </div>

          {/* Professional Tier */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-600 to-orange-600 rounded-lg blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative p-6 space-y-6 bg-white rounded-lg border border-gray-200 hover:border-transparent transition-all duration-300">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">Professional</h3>
                <p className="text-gray-500 text-sm">For large enterprises</p>
                <div className="text-3xl font-bold">$500<span className="text-lg font-normal">/mo</span></div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>40 resources per month</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>AI-powered insights</span>
                </li>
                <li className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  <span>Dedicated account manager</span>
                </li>
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                onClick={() => handleUpgrade('professional' as SubscriptionTier)}
              >
                Upgrade to Pro
              </Button>
            </div>
          </div>
        </div>
      </div>
      <SignUpDialog open={showSignUp} onOpenChange={setShowSignUp} />
      <LoginDialog open={showLogin} onOpenChange={setShowLogin} />
    </div>
  );
};